import type { UserConfig } from 'next-i18next'
import { appWithTranslation } from 'next-i18next'
import PlausibleProvider from 'next-plausible'
import type { AppProps } from 'next/app'

import '@nxt-insurance/ui-kit/assets/styles/fonts.css'
import '@nxt-insurance/ui-kit/assets/styles/reset.css'
import '@nxt-insurance/ui-kit/assets/styles/tailwind.css'

import { ConsentManager } from '@/components/ConsentManager'
import { CustomHead } from '@/components/CustomHead'
import { PageWrapper } from '@/components/PageWrapper'
import { PLAUSIBLE_DOMAINS } from '@/config/env'
import { AnalyticsProvider } from '@/context/AnalyticsContext'
import { FormattingProvider } from '@/context/FormattingContext'
import { RUMProvider } from '@/context/RUMContext'
import { trpc } from '@/utils/trpc'
import '@dotlottie/react-player/dist/index.css'
import { initFlowsI18n } from '@flows/core/i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import '@ui-kit/theme/unistyles'

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import nextI18NextConfig from '../../next-i18next.config'

// this is done to avoid multiple refetching until page refresh and run the query once
// https://medium.com/in-the-weeds/fetch-a-query-only-once-until-page-refresh-using-react-query-a333d00b86ff

const twentyFourHoursInMs = 1000 * 60 * 60 * 24
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: twentyFourHoursInMs,
    },
  },
})

const CustomApp = ({ Component, pageProps }: AppProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PlausibleProvider domain={PLAUSIBLE_DOMAINS}>
        <RUMProvider>
          <AnalyticsProvider>
            <FormattingProvider>
              <CustomHead />
              <PageWrapper>
                <ConsentManager />
                <Component {...pageProps} />
              </PageWrapper>
            </FormattingProvider>
          </AnalyticsProvider>
        </RUMProvider>
      </PlausibleProvider>
    </QueryClientProvider>
  )
}

export default trpc.withTRPC(
  appWithTranslation(CustomApp, { ...nextI18NextConfig, use: [initFlowsI18n] } as UserConfig)
)
